import React from "react";

/**
 * Compact P-Q quadrant indicator.
 * Q1: +kW +kVAR  Inductive (normal motor load - LAG)
 * Q2: -kW +kVAR  Exporting P, consuming Q (generator lagging)
 * Q3: -kW -kVAR  Exporting both (capacitive generation)
 * Q4: +kW -kVAR  Importing P, exporting Q (capacitors over-compensating - LEAD)
 */
function classify(kw, kvar) {
  if (kw == null || kvar == null) return { q: null, label: "—", tone: "text-zinc-400", accent: "#52525b" };
  if (kw >= 0 && kvar >= 0) return { q: 1, label: "Q1 · INDUCTIVE (LAG)", tone: "text-emerald-400", accent: "#10b981" };
  if (kw >= 0 && kvar < 0)  return { q: 4, label: "Q4 · LEADING (CAP. OVER-COMP.)", tone: "text-amber-400",   accent: "#f59e0b" };
  if (kw < 0  && kvar >= 0) return { q: 2, label: "Q2 · EXPORT (LAG GEN.)",        tone: "text-cyan-400",    accent: "#22d3ee" };
  return { q: 3, label: "Q3 · EXPORT (CAP. GEN.)",                                 tone: "text-violet-400",  accent: "#a78bfa" };
}

export default function QuadrantIndicator({ latest }) {
  const kw = latest?.ttl_kw;
  const kvar = latest?.ttl_kvar;
  const { q, label, tone, accent } = classify(kw, kvar);

  const tile = (num, x, y) => {
    const active = q === num;
    return (
      <rect
        key={num}
        x={x} y={y} width="28" height="28"
        rx="2"
        fill={active ? accent : "rgba(255,255,255,0.05)"}
        stroke={active ? accent : "rgba(255,255,255,0.12)"}
        strokeWidth="1"
        style={{
          filter: active ? `drop-shadow(0 0 8px ${accent})` : "none",
          transition: "fill 0.3s, stroke 0.3s, filter 0.3s",
        }}
      />
    );
  };

  return (
    <div
      className="flex items-center gap-4 p-3 border border-white/10 rounded-sm bg-white/[0.03] backdrop-blur-md"
      data-testid="quadrant-indicator"
    >
      <svg width="64" height="64" viewBox="0 0 64 64" aria-hidden="true">
        {/* Q2 top-left, Q1 top-right, Q3 bottom-left, Q4 bottom-right */}
        {tile(2, 2, 2)}
        {tile(1, 34, 2)}
        {tile(3, 2, 34)}
        {tile(4, 34, 34)}
        {/* Axis labels */}
        <text x="60" y="6" fontSize="6" fontFamily="JetBrains Mono" fill="#71717a" textAnchor="end">+Q</text>
        <text x="60" y="62" fontSize="6" fontFamily="JetBrains Mono" fill="#71717a" textAnchor="end">-Q</text>
        <text x="2" y="32" fontSize="6" fontFamily="JetBrains Mono" fill="#71717a">-P</text>
        <text x="62" y="32" fontSize="6" fontFamily="JetBrains Mono" fill="#71717a" textAnchor="end">+P</text>
      </svg>
      <div className="flex-1 min-w-0">
        <div className="label-tech">LOAD QUADRANT</div>
        <div className={`font-mono-tech text-xs mt-1 truncate ${tone}`} data-testid="quadrant-label">
          {label}
        </div>
        <div className="label-tech text-[9px] mt-1 text-zinc-500">
          kW {kw == null ? "—" : `${kw >= 0 ? "+" : ""}${Number(kw).toFixed(1)}`}
          {" · "}
          kVAR {kvar == null ? "—" : `${kvar >= 0 ? "+" : ""}${Number(kvar).toFixed(1)}`}
        </div>
      </div>
    </div>
  );
}
